import type { Team } from '@/types/game';
import { getTeamColors, normalizeTeamName } from '@/utils/game-utils';

interface TeamRankingsProps {
  teams: Team[];
  /** 'duel' = kompakte Karte unter dem Kopf, 'list' = ausführliche Liste. */
  variant?: 'duel' | 'list';
}

const rankPillStyles: Record<number, string> = {
  1: 'bg-rank-gold text-background',
  2: 'bg-rank-silver text-background',
  3: 'bg-rank-bronze text-background',
};

const TeamBlock = ({ team, align }: { team: Team; align: 'left' | 'right' }) => {
  const colors = getTeamColors(team.team);
  const rank = parseInt(team.rank, 10) || 0;
  const pill = rankPillStyles[rank] ?? 'bg-card-strong text-muted-foreground';

  return (
    <div className={`min-w-0 flex-1 ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <div className={`text-overline truncate ${colors.text}`}>Team {normalizeTeamName(team.team)}</div>
      <div className="text-score mt-1 text-[2rem] leading-none text-foreground">
        {(parseInt(team.score, 10) || 0).toLocaleString('de-DE')}
      </div>
      <span
        className={`text-overline mt-2 inline-block rounded-full px-2 py-0.5 ${pill}`}
      >
        Platz {rank || team.rank}
      </span>
    </div>
  );
};

const ShareBar = ({ teams }: { teams: Team[] }) => {
  const values = teams.map((t) => Math.max(parseInt(t.score, 10) || 0, 0));
  const total = values.reduce((a, b) => a + b, 0);

  return (
    <div className="mt-4 flex h-2 w-full gap-[3px]" role="presentation">
      {teams.map((team, i) => {
        const colors = getTeamColors(team.team);
        const pct = total > 0 ? (values[i] / total) * 100 : 100 / teams.length;
        return (
          <div
            key={team.team}
            className={`h-2 rounded-full bg-current ${colors.text}`}
            style={{ width: `${pct}%` }}
          />
        );
      })}
    </div>
  );
};

export const TeamRankings = ({ teams, variant = 'duel' }: TeamRankingsProps) => {
  if (!teams || teams.length <= 1) return null;

  const sorted = [...teams].sort(
    (a, b) => (parseInt(a.rank, 10) || 99) - (parseInt(b.rank, 10) || 99),
  );

  if (variant === 'list' || sorted.length > 2) {
    return (
      <div className="rounded-[20px] border border-border bg-card p-4">
        <div className="space-y-2">
          {sorted.map((team) => {
            const colors = getTeamColors(team.team);
            const rank = parseInt(team.rank, 10) || 0;
            const pill = rankPillStyles[rank] ?? 'bg-card-strong text-muted-foreground';
            return (
              <div key={team.team} className="flex items-center gap-3">
                <span className={`text-overline rounded-full px-2 py-0.5 ${pill}`}>{rank || team.rank}</span>
                <span className={`text-overline min-w-0 flex-1 truncate ${colors.text}`}>
                  Team {normalizeTeamName(team.team)}
                </span>
                <span className="text-score text-lg text-foreground">
                  {(parseInt(team.score, 10) || 0).toLocaleString('de-DE')}
                </span>
              </div>
            );
          })}
        </div>
        <ShareBar teams={sorted} />
      </div>
    );
  }

  return (
    <div className="rounded-[20px] border border-border bg-card p-4">
      <div className="flex items-start gap-3">
        <TeamBlock team={sorted[0]} align="left" />
        <span className="text-overline mt-2 flex-shrink-0 text-muted-foreground">VS</span>
        <TeamBlock team={sorted[1]} align="right" />
      </div>
      <ShareBar teams={sorted} />
    </div>
  );
};
